<template>
  <v-app id="v-app">
    <v-app-bar
        v-if="shouldDefaultComponents"
        class="site-header"
        color="site-chrome"
        elevation="0"
    >
      <button class="brand-link" type="button" @click="$router.push('/')" aria-label="StateMC Home">
        <img
            :src="utils.getImgUrl('logo-small.png')"
            class="small-image"
            alt="logo"
        >
        <span class="brand" v-if="breakpoint.smAndUp">
          <span class="color-primary">State</span>MC
        </span>
      </button>
      <nav class="desktop-nav" v-if="breakpoint.mdAndUp" aria-label="Hauptnavigation">
        <router-link to="/">{{ $t('home.title') }}</router-link>
        <router-link to="/join">{{ $t('join.button') }}</router-link>
        <router-link to="/rules" :class="{ 'router-link-active': route.name === 'Rules' }">{{ $t('rules.title') }}</router-link>
        <router-link to="/stats">{{ $t('stats.title') }}</router-link>
        <router-link to="/shop" class="shop-nav-link">
          <v-icon icon="mdi-crown-outline" size="17" />
          <span>{{ $t('shop.title') }}</span>
        </router-link>
      </nav>
      <v-spacer></v-spacer>
      <locale-switcher></locale-switcher>
      <v-btn
          v-if="breakpoint.mdAndUp"
          class="join-header-button ml-3"
          color="primary"
          variant="elevated"
          prepend-icon="mdi-controller-classic-outline"
          :to="{ name: 'Join', hash: '#join' }"
      >
        {{ $t('join.button') }}
      </v-btn>
      <v-app-bar-nav-icon aria-label="Menu" variant="text" @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
    </v-app-bar>
    <team-panel-navigator v-if="isTeamMember" />
    <v-navigation-drawer
        v-model="drawer"
        temporary
        location="right"
        class="site-drawer"
    >
      <template v-slot:prepend>
        <div class="drawer-head">
          <template v-if="isLoggedIn">
            <button
                class="drawer-account"
                type="button"
                :aria-expanded="accountMenuOpen"
                @click="accountMenuOpen = !accountMenuOpen"
            >
              <span class="account-avatar-wrap">
                <v-avatar rounded size="52" :image="utils.getMCAvatar(playerUUID)" />
                <span v-if="hasUnreadChats" class="account-dot" />
              </span>
              <span>
                <strong>{{ playerName || $t('auth.guest') }}</strong>
                <small>{{ $t('account.title') }}</small>
              </span>
              <v-icon :icon="accountMenuOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'" size="20" />
            </button>

            <v-expand-transition>
              <v-list v-if="accountMenuOpen" density="compact" nav class="drawer-account-menu">
                <v-list-item
                    to="/account"
                    role="option"
                    aria-label="account-details"
                    prepend-icon="mdi-card-account-details-outline"
                    :title="$t('account.links.details')"
                    value="account-details"
                    @click="drawer = false"
                ></v-list-item>
                <v-list-item
                    to="/chats"
                    role="option"
                    aria-label="chats"
                    prepend-icon="mdi-forum-outline"
                    :title="$t('chat.title', 2)"
                    value="Chats"
                    @click="drawer = false"
                >
                  <template v-slot:append>
                    <span v-if="hasUnreadChats" class="menu-unread-dot" />
                  </template>
                </v-list-item>
                <v-list-item
                    to="/notifications"
                    role="option"
                    aria-label="notifications"
                    prepend-icon="mdi-bell-alert"
                    :title="$t('notifications.title')"
                    value="Notifications"
                    @click="drawer = false"
                >
                  <template v-slot:append>
                    <span v-if="hasUnreadNotifications" class="menu-unread-dot" />
                  </template>
                </v-list-item>
                <v-list-item
                    role="option"
                    aria-label="logout"
                    prepend-icon="mdi-logout"
                    title="Logout"
                    value="logout"
                    @click="logout"
                ></v-list-item>
              </v-list>
            </v-expand-transition>
          </template>

          <div v-else class="drawer-login">
            <v-avatar rounded size="52">
              <v-icon icon="mdi-account-outline" size="34"></v-icon>
            </v-avatar>
            <v-btn color="primary" prepend-icon="mdi-login" @click="login">
              {{ $t('auth.login') }}
            </v-btn>
          </div>
        </div>
      </template>
      <v-list density="compact" nav aria-label="page">
        <v-divider class="my-2 mx-4" v-if="isLoggedIn" />
        <v-list-item
            v-for="item in drawerNavItems"
            :key="item.value"
            :to="item.to"
            role="option"
            :aria-label="item.value"
            :prepend-icon="item.drawerIcon"
            :title="$t(item.title)"
            :value="item.value"
            :active="item.active"
        ></v-list-item>
<!--        <v-list-item to="/changelog" role="option" aria-label="changelog" prepend-icon="mdi-note-text-outline" :title="$t('changelog.title')" value="changelog"></v-list-item>-->
      </v-list>
    </v-navigation-drawer>
    <v-card elevation="0" class="app-shell">
      <v-layout>
        <v-main
            :class="{ 'default-main': shouldDefaultComponents }"
            :style="shouldDefaultComponents ? 'margin-top: 64px;' : ''"
        >
          <banner-carousel
              v-if="shouldDefaultComponents && !route.meta.disableBanner"
              :compact="route.name !== 'Home'"
          />
          <div class="page-content" :class="{'mt-4': shouldDefaultComponents}">
            <router-view />
          </div>
          <state-footer v-if="shouldDefaultComponents" class="app-footer" />
          <error-dialog v-model="errorShowing" v-if="error" :title="error.title" :text="error.text" />
          <success-dialog v-model="successShowing" v-if="success" :title="success.title" :text="success.text" />
          <cookie-dialog v-if="shouldDefaultComponents" />
        </v-main>
      </v-layout>
    </v-card>
  </v-app>
</template>

<script setup>
import LocaleSwitcher from '@/components/LocaleSwitcher';
import { useStore } from 'vuex';
import BannerCarousel from '@/components/BannerCarousel';
import TeamPanelNavigator from '@/components/TeamPanelNavigator';
import ErrorDialog from '@/components/form/ErrorDialog';
import SuccessDialog from '@/components/form/SuccessDialog';
import { computed, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import CookieDialog from '@/components/form/CookieDialog';
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import StateFooter from '@/components/StateFooter.vue';
import { useDisplay } from 'vuetify';

const store = useStore();
const router = useRouter();
const route = useRoute();
const breakpoint = ref(useDisplay());

const isLoggedIn = computed(() => store.getters.isLoggedIn);
const playerName = computed(() => store.getters.playerName);
const playerUUID = computed(() => store.getters.playerUUID);
const isTeamMember = computed(() => store.getters.isTeamMember);
const error = computed(() => store.getters.error);
const success = computed(() => store.getters.success);
const locale = computed(() => store.getters.locale);

const errorShowing = computed({
  get () {
    return store.getters.errorShowing;
  },
  set (val) {
    store.commit('setErrorShowing', val);
  },
});

const successShowing = computed({
  get () {
    return store.getters.successShowing;
  },
  set (val) {
    store.commit('setSuccessShowing', val);
  },
});

const shouldDefaultComponents = computed(() => {
  return route.name !== 'CustomMainMenu';
});

const drawer = ref(false);
const accountMenuOpen = ref(false);
const primaryNavItems = computed(() => [
  {
    title: 'home.title',
    to: '/',
    value: 'home',
    active: route.name === 'Home',
    drawerIcon: 'mdi-home-city-outline',
  },
  {
    title: 'rules.title',
    to: '/rules',
    value: 'rules',
    active: route.name === 'Rules',
    drawerIcon: 'mdi-book-open-page-variant-outline',
  },
  {
    title: 'stats.title',
    to: '/stats',
    value: 'stats',
    active: route.path.startsWith('/stats'),
    drawerIcon: 'mdi-podium',
  },
  {
    title: 'shop.title',
    to: '/shop',
    value: 'shop',
    active: route.name === 'Shop',
    class: 'shop-nav-link',
    icon: 'mdi-crown-outline',
    drawerIcon: 'mdi-cart-outline',
  },
]);
const drawerNavItems = computed(() => [
  ...primaryNavItems.value,
  {
    title: 'join.button',
    to: '/join',
    value: 'join',
    active: route.name === 'Join',
    drawerIcon: 'mdi-controller-classic-outline',
  },
  {
    title: 'downloads.title',
    to: '/downloads',
    value: 'downloads',
    active: route.name === 'Downloads',
    drawerIcon: 'mdi-download-outline',
  },
  {
    title: 'wiki.title',
    to: '/wiki',
    value: 'wiki',
    active: route.path.startsWith('/wiki'),
    drawerIcon: 'mdi-wikipedia',
  },
]);

const setTempPath = (tempPath) => store.commit('setTempPath', tempPath);
const logout = async () => {
  drawer.value = false;
  accountMenuOpen.value = false;
  try {
    await store.dispatch('logout');
    window.location.assign('/');
  } catch (err) {
    store.commit('displayError', { text: `api-errors.${err.message}` });
  }
};

const login = () => {
  drawer.value = false;
  accountMenuOpen.value = false;
  if (route.name !== 'Auth') {
    setTempPath(route.path);
  }
  router.push('/auth/login');
};

onMounted(() => {
  if (locale.value) {
    document.documentElement.setAttribute('lang', locale.value);
  }
});

const hasUnreadChats = ref(false);
const hasUnreadNotifications = ref(false);
if (isLoggedIn.value) {
  const { onResult } = useQuery(gql`
        query {
          hasUnreadChats
          hasUnreadNotifications
        }
    `, {}, { pollInterval: 20000 });
  onResult((res) => {
    hasUnreadChats.value = res.data.hasUnreadChats;
    hasUnreadNotifications.value = res.data.hasUnreadNotifications;
  });
}

</script>

<style>
h1, h2, h3, h4, h5 {
  color: rgb(var(--v-theme-primary)) !important;
}

html {
  background: rgb(var(--v-theme-app-background));
}

body {
  background:
    radial-gradient(circle at top left, rgba(28, 176, 179, 0.14), transparent 32rem),
    linear-gradient(
      180deg,
      rgb(var(--v-theme-app-background)) 0%,
      rgb(var(--v-theme-app-background-soft)) 46%,
      rgb(var(--v-theme-app-background)) 100%
    );
}

.color-primary {
  color: rgb(var(--v-theme-primary)) !important;
}

.color-secondary {
  color: rgb(var(--v-theme-secondary)) !important;
}

.sm-view {
  max-width: 800px !important;
  margin: auto;
}

.m-view {
  max-width: 1200px !important;
  margin: auto;
}

.xm-view {
  max-width: 1400px !important;
  margin: auto;
}

.xxm-view {
  max-width: 1700px !important;
  margin: auto;
}

.v-dialog .v-overlay__content {
  margin: 0 !important;
}

.lg-padding {
  padding-right: 15px;
  padding-left: 15px;
}

.subtitle {
  opacity: 1;
  -webkit-line-clamp: unset !important;
}

@media screen and (max-width: 800px) {
  .lg-padding {
    padding-right: 0;
    padding-left: 0;
  }
}

.primary-lighter-text {
  color: rgb(var(--v-theme-primary-lighter)) !important;
}
.primary-boarder-2 {
  border: 2px solid rgb(var(--v-theme-primary-light)) !important;
}
.primary-boarder-1 {
  border: 1px solid rgb(var(--v-theme-primary-light)) !important;
}
</style>

<style scoped>
.brand {
  font-family: 'Poppins', sans-serif;
  font-weight: bold;
  font-size: 24px;
}

.app-shell {
  background: transparent;
  min-height: 100vh;
}

.app-shell :deep(.v-layout) {
  min-height: inherit;
}

.app-shell :deep(.v-main.default-main) {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px);
}

.page-content {
  flex: 1 0 auto;
}

.app-footer.site-footer {
  margin-top: auto;
}

.site-header {
  border-bottom: 1px solid rgba(124, 232, 234, 0.14);
  backdrop-filter: blur(18px);
}

.brand-link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  margin-left: 14px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 28px;
}

.desktop-nav a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 12px;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;
}

.desktop-nav a:hover,
.desktop-nav a.router-link-active {
  background: rgba(28, 176, 179, 0.13);
  color: white;
}

.desktop-nav a.shop-nav-link {
  border: 1px solid rgba(28, 176, 179, 0.28);
  background: rgba(28, 176, 179, 0.11);
  color: white;
}

.desktop-nav a.shop-nav-link:hover,
.desktop-nav a.shop-nav-link.router-link-active {
  border-color: rgba(124, 232, 234, 0.52);
  background: rgba(28, 176, 179, 0.2);
}

.drawer-shop-link {
  margin: 4px 8px;
  border: 1px solid rgba(28, 176, 179, 0.26);
  border-radius: 8px;
  background: rgba(28, 176, 179, 0.1);
}

.join-header-button {
  border-radius: 8px;
  font-weight: 800;
  letter-spacing: 0;
}

.menu-unread-dot {
  display: block;
  border-radius: 999px;
  background: rgb(var(--v-theme-error));
  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.16);
  width: 9px;
  height: 9px;
}

.account-avatar-wrap {
  position: relative;
  display: inline-flex;
}

.account-dot {
  position: absolute;
  right: -2px;
  top: -2px;
  display: block;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgb(var(--v-theme-error));
  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.16);
}

.site-drawer {
  border-left: 1px solid rgba(124, 232, 234, 0.16);
  background:
    linear-gradient(135deg, rgba(28, 176, 179, 0.12), transparent 38%),
    rgb(var(--v-theme-site-chrome)) !important;
}

.drawer-head {
  padding: 16px 14px 10px;
}

.drawer-account {
  display: inline-grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(124, 232, 234, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.drawer-account:hover {
  border-color: rgba(124, 232, 234, 0.48);
  background: rgba(28, 176, 179, 0.13);
}

.drawer-account strong {
  display: block;
  overflow: hidden;
  color: white;
  font-size: 14px;
  font-weight: 900;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawer-account small {
  display: block;
  overflow: hidden;
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawer-login {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 12px;
  border: 1px solid rgba(124, 232, 234, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
}

.site-drawer :deep(.v-list-item) {
  margin-inline: 8px;
  border-radius: 8px;
}

.drawer-account-menu {
  margin-top: 8px;
  padding: 4px 0 0;
  background: transparent;
}

.drawer-account-menu :deep(.v-list-item) {
  margin-left: 18px;
  padding-inline-start: 14px !important;
}

.site-drawer :deep(.v-list-item--active) {
  background: rgba(28, 176, 179, 0.15);
  color: white;
}

</style>
